// useElakaySwiper -- the single entry point through which every managed
// Swiper component gets its options:
//   page instance customization > Swiper Settings default > component origin
// (property by property, for the CURRENT device).
//
// Usage inside a component:
//   const swp = useElakaySwiper("productSlider", {
//     swiperOptions,                // per-device overrides of this instance
//     legacyTable,                  // component's historical responsive table
//     legacyExplicit,               // the page passed wm0..wm1200 itself
//     autoNavigation: infoSource?.navigation,
//     slideCount: products.length,
//   });
//   <Swiper key={swp.key} {...swp.swiperProps} className={`mine ${swp.className}`}
//           style={{ ...myStyle, ...swp.style }}>
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  A11y, Autoplay, EffectCards, EffectCoverflow, EffectCreative, EffectCube, EffectFade, EffectFlip,
  FreeMode, Grid, Keyboard, Mousewheel, Navigation, Pagination, Scrollbar,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import "swiper/css/effect-fade";
import "swiper/css/effect-cube";
import "swiper/css/effect-flip";
import "swiper/css/effect-coverflow";
import "swiper/css/effect-cards";
import "swiper/css/effect-creative";
import "swiper/css/grid";
import "swiper/css/free-mode";
import "./swiperSettings.css";
import { computeSwiper, deviceForWidth } from "./swiperSettingsModel";
import { getSwiperDefaultsSnapshot, loadSwiperDefaults, subscribeSwiperDefaults } from "./swiperSettingsStore";

export const SWIPER_MODULES = Object.freeze({
  A11y, Autoplay, EffectCards, EffectCoverflow, EffectCreative, EffectCube, EffectFade, EffectFlip,
  FreeMode, Grid, Keyboard, Mousewheel, Navigation, Pagination, Scrollbar,
});

const currentWidth = () => (typeof window !== "undefined" ? window.innerWidth : 1280);

// Current device, re-evaluated only when a breakpoint is crossed.
export const useSwiperDevice = () => {
  const [device, setDevice] = useState(() => deviceForWidth(currentWidth()));
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const update = () => setDevice(deviceForWidth(currentWidth()));
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return device;
};

export const useSwiperDefaults = () => {
  const snapshot = useSyncExternalStore(subscribeSwiperDefaults, getSwiperDefaultsSnapshot, getSwiperDefaultsSnapshot);
  useEffect(() => { loadSwiperDefaults(); }, []);
  return snapshot;
};

const useElakaySwiper = (componentId, {
  swiperOptions,
  legacyTable = null,
  legacyExplicit = false,
  autoNavigation = true,
  slideCount = Infinity,
  originPatch,
  deviceOverride,
} = {}) => {
  const responsiveDevice = useSwiperDevice();
  const device = deviceOverride || responsiveDevice;
  const { components } = useSwiperDefaults();
  const defaults = components?.[componentId];
  const legacyKey = legacyTable ? JSON.stringify(legacyTable) : "";
  const instanceKey = swiperOptions ? JSON.stringify(swiperOptions) : "";
  const originPatchKey = originPatch ? JSON.stringify(originPatch) : "";

  return useMemo(() => {
    const computed = computeSwiper({
      componentId,
      device,
      defaults,
      instance: swiperOptions,
      legacyTable,
      legacyExplicit,
      autoNavigation,
      modules: SWIPER_MODULES,
      slideCount,
      originPatch,
    });
    return {
      device,
      key: `${componentId}|${computed.structuralKey}`,
      swiperProps: computed.props,
      className: computed.className,
      style: computed.style,
      resolved: computed.resolved,
    };
    // legacyTable / swiperOptions are compared by value (their JSON keys).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, device, defaults, instanceKey, legacyKey, originPatchKey, legacyExplicit, autoNavigation, slideCount]);
};

export default useElakaySwiper;
